import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type MeResponse } from '../api/client';
import { CustomerPicker } from './CustomerPicker';

interface AppHeaderProps {
  me?: MeResponse;
}

export function AppHeader({ me }: AppHeaderProps) {
  const location = useLocation();
  const handleLogout = async () => {
    await api.logout();
    window.location.assign('/');
  };

  const showAuditLink = me?.canViewAuditDashboard === true;
  const onAuditPage = location.pathname === '/portal/audit';
  const onPortalRoot = location.pathname.startsWith('/portal') && !onAuditPage;

  const companyName = me ? (me.customer.displayName ?? me.customer.slug) : '';
  const userName = me ? (me.displayName ?? me.email) : '';

  return (
    <header className="relative border-b border-wc-muted/15 bg-gradient-to-r from-wc-white via-wc-white to-wc-blue/[0.04] shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
      <div className="flex items-center justify-between gap-4 px-6 py-3">
        <a href="/" className="group flex items-center gap-3" aria-label="Weigandt">
          <img
            src="/weigandt-logo.svg"
            alt="Weigandt"
            className="h-7 w-auto transition-transform group-hover:scale-105"
          />
          <span className="hidden text-[11px] font-semibold uppercase tracking-[0.2em] text-wc-blue/90 sm:inline">
            Customer portal
          </span>
        </a>
        {me && (
          <div className="flex items-center gap-2 sm:gap-3">
            {showAuditLink && (
              <>
                <nav className="hidden items-center gap-1 sm:flex" aria-label="Portal sections">
                  <Link
                    to="/portal"
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      onPortalRoot
                        ? 'bg-wc-blue/10 text-wc-blue'
                        : 'text-wc-muted-strong hover:bg-wc-gray/70 hover:text-wc-blue'
                    }`}
                  >
                    Documents
                  </Link>
                  <Link
                    to="/portal/audit"
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      onAuditPage
                        ? 'bg-wc-blue/10 text-wc-blue'
                        : 'text-wc-muted-strong hover:bg-wc-gray/70 hover:text-wc-blue'
                    }`}
                  >
                    Audit
                  </Link>
                </nav>
                <div className="mx-1 hidden h-8 w-px bg-wc-muted/20 sm:block" aria-hidden />
              </>
            )}
            <div className="hidden items-center gap-3 sm:flex">
              <div className="min-w-0 text-left leading-tight">
                {me.isGlobalUser ? (
                  <ActiveCustomerDropdown me={me} companyName={companyName} />
                ) : (
                  <div
                    className="max-w-[15rem] truncate font-serif text-sm font-semibold text-wc-black"
                    title={companyName}
                  >
                    {companyName}
                  </div>
                )}
                <div
                  className="max-w-[15rem] truncate text-xs text-wc-muted-strong"
                  title={userName}
                >
                  {userName}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-full border border-wc-muted/25 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-wc-muted-strong transition-colors hover:border-wc-red/40 hover:bg-wc-red/5 hover:text-wc-red"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

interface ActiveCustomerDropdownProps {
  me: MeResponse;
  companyName: string;
}

/**
 * Inline customer picker rendered in the header for global users. Shows
 * the active company name as the prominent line; clicking opens a popover
 * with the full list so the user can switch tenants without leaving the page.
 */
function ActiveCustomerDropdown({ me, companyName }: ActiveCustomerDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="group inline-flex max-w-[15rem] items-center gap-1 font-serif text-sm font-semibold text-wc-black transition-colors hover:text-wc-blue"
        title={`${companyName} — switch customer`}
      >
        <span className="truncate">{companyName}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`shrink-0 text-wc-muted-strong transition-transform group-hover:text-wc-blue ${
            open ? 'rotate-180' : ''
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2" role="menu">
          <CustomerPicker
            variant="menu"
            activeCustomerId={me.customer.id}
            homeCustomerId={me.homeCustomer.id}
            onPicked={() => {
              setOpen(false);
              // Force a full reload so every page's data refetches with
              // the new session active customer. Simpler than threading
              // a queryClient.invalidate down to every component.
              window.location.assign('/portal');
            }}
          />
        </div>
      )}
    </div>
  );
}
